import type { Lead, LeadFieldDefinition, LeadFieldValue, Priority } from "../../lib/types";

/**
 * Definição única dos campos de um lead.
 *
 * Criação, edição rápida e edição completa partem daqui: mesma composição,
 * mesmos obrigatórios, mesma normalização e as mesmas mensagens de erro, como
 * a regra de equivalência do lead exige (AGENTS §19.1, §25.4). Um campo novo
 * entra nesta definição e aparece nas três superfícies de uma vez.
 *
 * Os campos-base são exatamente os dez fixos da §7.1: nome, empresa, função,
 * valor potencial, prioridade, e-mail, telefone, Instagram, segmento e origem.
 * Todo o resto — cidade, estado, site, dores, objeções — é definição do
 * formulário do funil e chega pelo store; duplicá-los como campo fixo criaria
 * duas verdades para o mesmo dado.
 */

export const LEAD_FORM_STEPS = [
  {
    id: "identificacao",
    label: "Identificação",
    hint: "Quem é, o que faz, de onde veio e quanto vale.",
  },
  { id: "contato", label: "Contato", hint: "Por onde falar com essa pessoa." },
  {
    id: "negocio",
    label: "Negócio",
    hint: "Marcadores e o contexto da conversa.",
  },
] as const;

export type LeadFormStepId = (typeof LEAD_FORM_STEPS)[number]["id"];

export type LeadFieldValues = Record<string, LeadFieldValue["value"]>;

/**
 * Classificação escolhida por eixo: `definitionId` → `optionId`, e string vazia
 * quando o eixo ficou sem resposta. O protótipo tem um eixo só ("Classificação",
 * alimentado pelo catálogo); o domínio admite vários, e o formulário mostra um
 * seletor para cada um — mesma regra, generalizada (AGENTS §19.4).
 */
export type LeadClassificationChoice = Record<string, string>;

export interface LeadFormValues {
  name: string;
  company: string;
  role: string;
  segment: string;
  source: string;
  priority: Priority;
  email: string;
  phone: string;
  instagram: string;
  potentialValue: string;
  tagIds: string[];
  triggerIds: string[];
  fieldValues: LeadFieldValues;
  classifications: LeadClassificationChoice;
}

export function emptyLeadForm(): LeadFormValues {
  return {
    name: "",
    company: "",
    role: "",
    segment: "",
    source: "",
    priority: "medium",
    email: "",
    phone: "",
    instagram: "",
    potentialValue: "",
    tagIds: [],
    triggerIds: [],
    fieldValues: {},
    classifications: {},
  };
}

/** Preenche o formulário a partir de um lead salvo, para a edição. */
export function leadFormFromLead(
  lead: Lead,
  fieldValues: LeadFieldValues = {},
  classifications: LeadClassificationChoice = {},
): LeadFormValues {
  return {
    ...emptyLeadForm(),
    name: lead.name,
    company: lead.company,
    role: lead.role,
    segment: lead.segment,
    source: lead.source,
    priority: lead.priority,
    email: lead.email ?? "",
    phone: lead.phone ?? "",
    instagram: lead.instagram ?? "",
    // Zero é valor gravado, não ausência: com o campo obrigatório (§24), tratar
    // 0 como vazio travaria a edição de um lead que já vale zero.
    potentialValue: String(lead.potentialValue ?? ""),
    tagIds: [...lead.tagIds],
    triggerIds: [...lead.triggerIds],
    fieldValues: { ...fieldValues },
    classifications: { ...classifications },
  };
}

/**
 * Converte "R$ 12.500,00" em 12500. Valor potencial é inteiro não negativo e
 * a formatação é separada do valor persistido (AGENTS §7.6).
 */
export function parseMoney(input: string): number | null {
  const text = input.trim();
  if (!text) return 0;
  const digits = text.replace(/[^\d,.-]/g, "");
  if (!digits) return null;
  const normalized = digits.replace(/\./g, "").replace(",", ".");
  const value = Number(normalized);
  if (!Number.isFinite(value) || value < 0) return null;
  return Math.round(value);
}

const REQUIRED_BASE: Array<{ key: keyof LeadFormValues; label: string; step: LeadFormStepId }> = [
  { key: "name", label: "Nome", step: "identificacao" },
  { key: "company", label: "Empresa", step: "identificacao" },
  { key: "role", label: "Função", step: "identificacao" },
  { key: "segment", label: "Segmento", step: "identificacao" },
  { key: "source", label: "Origem", step: "identificacao" },
];

export type LeadFormErrors = Record<string, string>;

/** Campos do funil que entram no formulário, na ordem das seções. */
export function formFieldsOf(
  fields: readonly LeadFieldDefinition[],
  funnelId: string,
): LeadFieldDefinition[] {
  return fields
    .filter((field) => field.funnelId === funnelId && field.inForm && !field.archived)
    .sort((a, b) => a.order - b.order);
}

export function validateLeadForm(
  values: LeadFormValues,
  fields: readonly LeadFieldDefinition[],
): LeadFormErrors {
  const errors: LeadFormErrors = {};
  for (const required of REQUIRED_BASE) {
    if (!String(values[required.key] ?? "").trim()) {
      errors[required.key] = `${required.label} é obrigatório.`;
    }
  }
  if (!values.potentialValue.trim()) {
    // O Zênite marca o valor potencial como obrigatório (§24); em branco não vale zero.
    errors.potentialValue = "Informe o valor potencial.";
  } else if (parseMoney(values.potentialValue) === null) {
    errors.potentialValue = "Informe um valor inteiro não negativo.";
  }
  if (values.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) {
    errors.email = "E-mail inválido.";
  }
  for (const field of fields) {
    if (!field.required) continue;
    const value = values.fieldValues[field.id];
    const empty =
      value === undefined ||
      value === null ||
      value === "" ||
      (Array.isArray(value) && value.length === 0);
    if (empty) errors[field.id] = `${field.name} é obrigatório.`;
  }
  return errors;
}

/** Em qual etapa do diálogo cada erro aparece, para levar o foco ao lugar certo. */
export function stepOfError(
  key: string,
  fields: readonly LeadFieldDefinition[],
): LeadFormStepId {
  const base = REQUIRED_BASE.find((item) => item.key === key);
  if (base) return base.step;
  if (key === "potentialValue" || key === "priority") return "identificacao";
  if (["email", "phone", "instagram"].includes(key)) return "contato";
  if (fields.some((field) => field.id === key)) return "negocio";
  return "negocio";
}

export interface LeadDraft {
  lead: Omit<Lead, "id" | "createdAt" | "lastInteractionAt">;
  fieldValues: LeadFieldValues;
}

export function leadFormToDraft(values: LeadFormValues): LeadDraft {
  const trimmed = (value: string) => value.trim();
  const optional = (value: string) => (value.trim() ? value.trim() : undefined);
  return {
    lead: {
      name: trimmed(values.name),
      company: trimmed(values.company),
      role: trimmed(values.role),
      segment: trimmed(values.segment),
      source: trimmed(values.source),
      priority: values.priority,
      potentialValue: parseMoney(values.potentialValue) ?? 0,
      email: optional(values.email),
      phone: optional(values.phone),
      instagram: optional(values.instagram),
      tagIds: [...values.tagIds],
      triggerIds: [...values.triggerIds],
    },
    fieldValues: { ...values.fieldValues },
  };
}

